import { createContext, useCallback, useContext, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Button } from '../components/ui'
import './confirm.css'

/* App-wide "are you sure?" dialog — replaces ad-hoc window.confirm() calls
 * (which look like browser chrome, can't be styled, and block the whole
 * tab) with one styled modal shared by every consequential action:
 * delete, suspend, dissolve, reject, cancel, remove, deactivate, approve
 * with exceptions, sign out, etc. Mount <ConfirmHost /> once near the app
 * root (see main.tsx), then call `const confirm = useConfirm()` anywhere
 * and `if (await confirm({ title, message, destructive: true })) { ... }`. */

export interface ConfirmOptions {
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Red confirm button + tone, for anything destructive/hard-to-reverse. */
  destructive?: boolean
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void
}

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const busyRef = useRef(false)

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve })
    })
  }, [])

  function settle(ok: boolean) {
    if (busyRef.current) return
    busyRef.current = true
    pending?.resolve(ok)
    setPending(null)
    setTimeout(() => { busyRef.current = false }, 0)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <div className="confirm-overlay" role="presentation" onClick={() => settle(false)}>
          <div
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="confirm-title" className="confirm-dialog__title">{pending.title}</h2>
            {pending.message && <div className="confirm-dialog__message">{pending.message}</div>}
            <div className="confirm-dialog__actions">
              <Button variant="ghost" onClick={() => settle(false)} autoFocus>
                {pending.cancelLabel ?? 'Cancel'}
              </Button>
              <Button
                variant={pending.destructive ? 'danger' : 'primary'}
                onClick={() => settle(true)}
              >
                {pending.confirmLabel ?? 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

/** Returns an async confirm(opts) function. Resolves true/false — never
 * throws, so callers can `if (!(await confirm(...))) return`. */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm() must be used within <ConfirmProvider>')
  }
  return ctx
}
